import { Box, Flex, Image, Text, useDisclosure } from "@chakra-ui/react";
import { ReactNode } from "react";
import React, { useCallback } from "react";
import { useLyricsTheme } from "../../context/LyricsThemeContext";
import { useMusicService } from "../../context/MusicServiceContext";
import LyricTimingControls from "../LyricTimingControls";
import ProgressBar from "../ProgressBar";
import LyricsSettingsModal from "../LyricsSettingsModal";
import SpotifyControls from "../SpotifyControls";
import SpotifyAttribution from "../SpotifyAttribution";

interface TeslaLayoutProps {
  lyricsContent: ReactNode;
  playbackState: any;
  progressMs: number;
  duration: number;
  startProgressTimer: (startTime: number, isPlaying: boolean) => void;
  setProgressMs: (time: number) => void;
  accentColor: string;
  lyricsDelay: number;
  setLyricsDelay: (delay: number) => void;
  layoutMode: 'split' | 'tesla';
  setLayoutMode: (mode: 'split' | 'tesla') => void;
  onSettingsOpen?: () => void;
}

const TeslaLayout = ({
  lyricsContent,
  playbackState,
  progressMs,
  duration,
  startProgressTimer,
  setProgressMs,
  accentColor,
  lyricsDelay,
  setLyricsDelay,
  layoutMode,
  setLayoutMode,
  onSettingsOpen,
}: TeslaLayoutProps) => {
  const { isLightMode, backgroundType, backgroundColor } = useLyricsTheme();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isSpotifySelected } = useMusicService();
  
  const handleSettingsClick = () => {
    console.log("TeslaLayout: calling local onOpen function");
    onOpen();
  };

  return (
    <Box 
      className={`${isLightMode ? 'light-mode' : 'dark-mode'} tesla-mode tesla-layout`}
      minH="100%"
      height="auto"
      color="var(--color-text-primary)"
      position="relative"
      overflow="hidden"
      pt="60px"
    >
      {/* Main content area */}
      <Box 
        h="calc(100vh - 80px)" 
        overflow="hidden"
        pt={4}
        px={4}
        className="lyrics-section"
        pb="130px"
      >
        {/* Use cloneElement to inject the onSettingsOpen prop */}
        {React.cloneElement(lyricsContent as React.ReactElement, { onSettingsOpen: handleSettingsClick })}
      </Box>

      {/* Bottom Player Bar */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        height="auto"
        bg="var(--color-background-muted)"
        backdropFilter="blur(20px)"
        borderTop="1px solid"
        borderColor="var(--tesla-border)"
        zIndex={5}
      >
        <Box position="relative" width="100%">
          {/* Spotify Attribution (only when Spotify is selected) */}
          {isSpotifySelected && playbackState && (
          <SpotifyAttribution playbackState={playbackState} />
        )}
        </Box>
        <Flex direction="column" height="100%">
          {/* Top row: Unified Album info and Lyric Delay UI */}
          <Flex 
            align="center" 
            mt={1}
            borderRadius="lg"
            overflow="hidden"
            height="130px"
            justifyContent="space-between"
          >
            {/* Album and Track Info */}
            <Flex 
              height="100%" 
              align="center" 
              flex={1}
              pr={5}
              ml={3}
              width="25%"
              justifyContent="left"
            >
              <Image 
                src={playbackState?.item?.album?.images[0]?.url || ''} 
                alt={playbackState?.item?.album?.name || 'Album cover'}
                boxSize="100px"
                borderRadius="md"
                objectFit="cover"
                mr={2}
                boxShadow="0 2px 8px rgba(0, 0, 0, 0.3)"
                fallbackSrc="https://via.placeholder.com/70"
              />
              <Flex direction="column" align="left">
                <Text 
                  fontWeight="bold" 
                  fontSize="xl" 
                  align="left"
                  noOfLines={1} 
                  color="var(--color-text-primary)"
                >
                  {playbackState?.item?.name || 'No Track Playing'}
                </Text>
                <Text 
                  fontSize="xs" 
                  color="var(--tesla-text-secondary)" 
                  noOfLines={1}
                  align="left"
                >
                  {playbackState?.item?.artists 
                    ? playbackState.item.artists.map((artist: any) => artist.name).join(', ') 
                    : 'Unknown Artist'}
                </Text>
                <Box display="flex" justifyContent="left" width="80%">
                  <SpotifyControls />
                </Box>
              </Flex>
            </Flex>
            <Flex 
              width="50%" 
              height="100%" 
              align="center" 
              justify="center"
              flexDirection="column"
            >
              <LyricTimingControls 
                lyricsDelay={lyricsDelay}
                setLyricsDelay={setLyricsDelay}
                compact={false}
              />
              {/* Bottom row: Progress bar and controls */}
              <ProgressBar
                playbackState={playbackState}
                progressMs={progressMs}
                duration={duration}
                startProgressTimer={startProgressTimer}
                setProgressMs={setProgressMs}
                accentColor={accentColor}
                isFullWidth={true}
              />
            </Flex>
            <Flex width="25%"></Flex>
          </Flex>
        </Flex>
      </Box>
      
      {/* Add Settings Modal with our local disclosure state */}
      <LyricsSettingsModal 
        isOpen={isOpen} 
        onClose={onClose}
        layoutMode={layoutMode}
        setLayoutMode={setLayoutMode}
      />
    </Box>
  );
};

export default TeslaLayout;